/**
 * Перетаскивание файлов на форму целиком (без отдельной дропзоны в разметке).
 *
 * Каждый FileAttach регистрирует себя с областью формы (та же, что для Ctrl+V).
 * Слушатели drag-событий висят на document один раз на всё приложение и
 * выбирают получателя:
 * - область должна содержать элемент под курсором;
 * - при вложенных областях побеждает самая глубокая;
 * - при нескольких FileAttach в одной области — смонтированный последним
 *   (форма отчёта в карточке задачи монтируется по v-if и перехватывает файлы,
 *   пока открыта).
 *
 * Пока над формой тащат файл — поверх неё некликабельный оверлей-подсказка.
 * После броска оверлей прячется, файлы добавляются в FileAttach и видны
 * в его штатном списке.
 * Перетаскивание не-файлов (текст, строки, DnD сотрудников) игнорируется.
 */
import { computed, onMounted, onUnmounted, reactive } from 'vue'

type TRect = { top: number, left: number, width: number, height: number }

interface IFileDropEntry {
  id: number
  getScope: () => HTMLElement | null
  isDisabled: () => boolean
  addFiles: (files: File[]) => void
}

const entries = new Map<number, IFileDropEntry>()

const state = reactive({
  /** Получатель, над областью которого сейчас тащат файл */
  dragId: null as number | null,
  dragRect: null as TRect | null,
})

let nextId = 0
let listenersInstalled = false
let hideTimer: ReturnType<typeof setTimeout> | null = null

/* dragover приходит непрерывно, пока файл над страницей; его отсутствие — признак ухода/отмены */
const DRAG_IDLE_MS = 400

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files')

const getDepth = (el: HTMLElement) => {
  let depth = 0
  for (let node: HTMLElement | null = el; node; node = node.parentElement)
    depth++
  return depth
}

const toRect = (el: HTMLElement): TRect => {
  const rect = el.getBoundingClientRect()

  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
}

const resolveEntry = (target: EventTarget | null): IFileDropEntry | null => {
  if (!(target instanceof Node))
    return null

  let best: IFileDropEntry | null = null
  let bestDepth = -1

  for (const entry of entries.values()) {
    if (entry.isDisabled())
      continue
    const scope = entry.getScope()
    if (!scope?.contains(target))
      continue
    const depth = getDepth(scope)
    if (depth > bestDepth || (depth === bestDepth && best && entry.id > best.id)) {
      best = entry
      bestDepth = depth
    }
  }

  return best
}

const resetDrag = () => {
  if (hideTimer)
    clearTimeout(hideTimer)
  hideTimer = null
  state.dragId = null
  state.dragRect = null
}

const onDragOver = (e: DragEvent) => {
  if (!hasFiles(e))
    return

  const entry = resolveEntry(e.target)
  if (!entry) {
    resetDrag()
    return
  }

  e.preventDefault()
  if (e.dataTransfer)
    e.dataTransfer.dropEffect = 'copy'

  state.dragId = entry.id
  state.dragRect = toRect(entry.getScope()!)

  if (hideTimer)
    clearTimeout(hideTimer)
  hideTimer = setTimeout(resetDrag, DRAG_IDLE_MS)
}

const onDragLeave = (e: DragEvent) => {
  // relatedTarget === null — курсор ушёл за пределы окна или drag отменён
  if (!e.relatedTarget)
    resetDrag()
}

const onDrop = (e: DragEvent) => {
  if (!hasFiles(e))
    return

  const entry = resolveEntry(e.target)

  resetDrag()
  if (!entry)
    return

  e.preventDefault()
  const files = Array.from(e.dataTransfer?.files ?? [])
  if (!files.length)
    return

  entry.addFiles(files)
}

const installListeners = () => {
  if (listenersInstalled)
    return
  listenersInstalled = true
  document.addEventListener('dragover', onDragOver)
  document.addEventListener('dragleave', onDragLeave)
  document.addEventListener('drop', onDrop)
}

export function useFormFileDrop(options: Omit<IFileDropEntry, 'id'>) {
  const id = ++nextId
  const entry: IFileDropEntry = { id, ...options }

  onMounted(() => {
    entries.set(id, entry)
    installListeners()
  })

  onUnmounted(() => {
    entries.delete(id)
    if (state.dragId === id)
      resetDrag()
  })

  return {
    /** Область формы для оверлея, пока над ней тащат файл для этого получателя */
    rect: computed(() => (state.dragId === id ? state.dragRect : null)),
  }
}
